<template>
  <div class="moov-card-form">
    <moov-form
      ref="moovFormRef"
      name="card-form"
      method="POST"
      :action="formAction"
    />
    <k-input
      label="Name on card"
      v-model="holderName"
      :error="errors.holderName"
      :show-error-message="true"
      class="k-input--moov"
      @input="checkNameValidity"
    />
    <k-input
      label="Card number"
      :error="errors.cardNumber"
      :show-error-message="true"
      class="k-input--moov">
      <moov-card-number-input
        ref="cardNumberRef"
        formname="card-form"
        name="cardNumber"
        required
      />
    </k-input>
    <div class="moov-card-form__expiry-cvc-container">
      <k-input
        label="Expiry date"
        :error="errors.cardExpiry"
        :show-error-message="true"
        class="k-input--moov">
        <moov-expiration-date-input
          ref="cardExpiryRef"
          formname="card-form"
          name="expiration"
          required
        />
      </k-input>
      <k-input
        label="CVC"
        :error="errors.cardCvc"
        :show-error-message="true"
        class="k-input--moov">
        <moov-card-security-code-input
          ref="cardCvcRef"
          formname="card-form"
          name="cardCvv"
          required
        />
      </k-input>
      <k-input
        label="ZIP code"
        v-model="postalCode"
        :error="errors.postalCode"
        :show-error-message="true"
        class="k-input--moov"
        @input="checkPostalCodeValidity"
      />
    </div>
    <div v-if="error" class="moov-card-form__error">
      {{ error }}
    </div>
  </div>
</template>

<script>
import {
  onMounted, ref, reactive, nextTick, watch,
} from 'vue';
import { useStore } from 'vuex';
import KInput from '@components/inputs/input.vue';
import { regexPatterns, axios } from '@utils';

const MOOV_JS_URL = 'https://js.moov.io/v1';

function loadMoovScript() {
  if (window.Moov) return Promise.resolve();

  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${MOOV_JS_URL}"]`);
    if (existing) {
      existing.addEventListener('load', resolve);
      existing.addEventListener('error', reject);
      return;
    }

    const script = document.createElement('script');
    script.type = 'text/javascript';
    script.src = MOOV_JS_URL;
    script.addEventListener('load', resolve);
    script.addEventListener('error', reject);
    document.head.appendChild(script);
  });
}

export default {
  name: 'MoovCardForm',
  components: {
    KInput,
  },
  props: {
    accountId: {
      type: String,
      default: '',
    },
  },
  emits: ['ready', 'success', 'error'],
  setup(props, { emit }) {
    const store = useStore();

    const moovFormRef = ref(null);
    const cardNumberRef = ref(null);
    const cardExpiryRef = ref(null);
    const cardCvcRef = ref(null);
    const error = ref(null);
    const moovToken = ref(null);
    const moovAccountId = ref(null);
    const isMoovFormValid = ref(false);
    const isInternalFormValid = ref(false);
    const holderName = ref('');
    const postalCode = ref('');

    const errors = reactive({
      holderName: null,
      cardNumber: null,
      cardExpiry: null,
      cardCvc: null,
      postalCode: null,
    });

    const clearErrors = () => {
      error.value = null;
      errors.holderName = null;
      errors.cardNumber = null;
      errors.cardExpiry = null;
      errors.cardCvc = null;
      errors.postalCode = null;
    };

    const formAction = ref('');

    const fetchMoovToken = async () => {
      try {
        const userId = store.state.user.current?.id;
        const params = userId ? { user_id: userId } : {};
        const { data } = await axios.get('/api/v1/moov/token.json', { params });
        moovToken.value = data.token;
        moovAccountId.value = data.account_id;
        return data;
      } catch (err) {
        error.value = err.response?.data?.error || err.message || 'Failed to initialize payment form. Please refresh and try again.';
        return null;
      }
    };

    const initializeMoov = async () => {
      try {
        await loadMoovScript();

        const tokenData = await fetchMoovToken();
        if (!tokenData) return;

        const accountId = props.accountId || tokenData.account_id;
        formAction.value = `/accounts/${accountId}/cards`;

        await nextTick();

        const form = moovFormRef.value;
        if (form) {
          form.requestHeaders = {
            'content-type': 'application/json',
            Authorization: `Bearer ${moovToken.value}`,
          };

          form.onSuccess = async (response) => {
            const cardData = await response.json();
            clearErrors();
            emit('success', cardData);
          };

          form.onError = async (response) => {
            try {
              const errorData = await response.json();

              if (errorData.cardNumber) {
                errors.cardNumber = errorData.cardNumber;
              }
              if (errorData.expiration) {
                errors.cardExpiry = errorData.expiration;
              }
              if (errorData.cardCvv) {
                errors.cardCvc = errorData.cardCvv;
              }
              if (errorData.billingAddress) {
                errors.postalCode = errorData.billingAddress.replace('postalCode: ', '');
              }

              const msg = 'Card information is invalid!';
              error.value = msg;
            } catch (err) {
              console.error('Failed to parse error response:', err);
              error.value = 'An error occurred submitting the card';
            }

            emit('error');
          };

          form.onCheckValidity = (isValid) => {
            isMoovFormValid.value = isValid;
          };

          const cardNumberInput = cardNumberRef.value;
          if (cardNumberInput) {
            cardNumberInput.onCheckValidity = (isValid) => {
              if (!isValid) {
                errors.cardNumber = 'Invalid card number';
              } else {
                errors.cardNumber = null;
              }
            };
            cardNumberInput.onInput = () => {
              cardNumberInput.checkValidity();
              form.checkValidity();
            };
          }

          const expiryInput = cardExpiryRef.value;
          if (expiryInput) {
            expiryInput.onCheckValidity = (isValid) => {
              if (!isValid) {
                errors.cardExpiry = 'Invalid expiry date';
              } else {
                errors.cardExpiry = null;
              }
            };
            expiryInput.onInput = () => {
              expiryInput.checkValidity();
              form.checkValidity();
            };
          }

          const cvcInput = cardCvcRef.value;
          if (cvcInput) {
            cvcInput.onCheckValidity = (isValid) => {
              if (!isValid) {
                errors.cardCvc = 'Invalid CVC';
              } else {
                errors.cardCvc = null;
              }
            };
            cvcInput.onInput = () => {
              cvcInput.checkValidity();
              form.checkValidity();
            };
          }
        }
      } catch (err) {
        error.value = 'Failed to load payment form. Please refresh and try again.';
      }
    };

    const setInternalFormValidity = (name, zipCode) => {
      isInternalFormValid.value = name.trim()
        && regexPatterns.zipcode.regex.test(zipCode.trim());
    };

    const checkNameValidity = (name) => {
      if (!name.trim()) {
        errors.holderName = 'Name on card is required';
      } else {
        errors.holderName = null;
      }
      setInternalFormValidity(name, postalCode.value);
    };

    const checkPostalCodeValidity = (zipCode) => {
      if (!zipCode.trim()) {
        errors.postalCode = 'ZIP code is required';
      } else if (!regexPatterns.zipcode.regex.test(zipCode.trim())) {
        errors.postalCode = 'Invalid ZIP code format';
      } else {
        errors.postalCode = null;
      }
      setInternalFormValidity(holderName.value, zipCode);
    };

    const submitCardForm = async () => {
      const form = moovFormRef.value;
      if (!form) return null;

      try {
        setInternalFormValidity(holderName.value, postalCode.value);
        if (!isInternalFormValid.value || !isMoovFormValid.value) {
          error.value = 'Please fill in all required fields and correct any errors';
          return null;
        }

        form.requestBody = {
          ...form.requestBody,
          holderName: holderName.value.trim(),
          billingAddress: {
            postalCode: postalCode.value.trim(),
          },
        };

        await form.requestSubmit();
      } catch (err) {
        error.value = err.message || 'Failed to submit card data';
        return null;
      }
    };

    watch([isMoovFormValid, isInternalFormValid], ([moovValid, internalValid]) => {
      emit('ready', moovValid && internalValid);
    });

    onMounted(() => {
      initializeMoov();
    });

    return {
      moovFormRef,
      cardNumberRef,
      cardExpiryRef,
      cardCvcRef,
      error,
      errors,
      holderName,
      postalCode,
      formAction,
      clearErrors,
      submitCardForm,
      checkNameValidity,
      checkPostalCodeValidity,
    };
  },
};
</script>

<style lang="scss" scoped>
.moov-card-form {
  width: 100%;

  & > * {
    width: 100%;
  }

  &__expiry-cvc-container {
    display: flex;

    & > * {
      min-width: 80px;

      &:not(:last-child) {
        min-width: 120px;
        margin-right: 20px;
      }
    }
  }

  &__error {
    color: var(--rust-50);
    font-size: 14px;
    margin-top: calc(var(--grid-unit) * 2);
  }
}

.k-input--moov {
  margin-bottom: var(--grid-unit);

  :deep(iframe) {
    max-height: calc(var(--grid-unit) * 4);
    padding: 4px 12px;
  }
}
</style>
